import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

interface State {
}

const defState: State = {
};

interface SearchParams {
  stockDate: string,
  wh: string,
  pnLike: string,
  snLike: string,
  trNoLike: string,
  matchYn: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  stockDate: dayjs().format("YYYYMMDD"),
  wh: "",
  pnLike: "",
  snLike: "",
  trNoLike: "",
  matchYn: "",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

function InventoryCompare(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "창고", field: "wh", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 180, cellStyle: {textAlign: "left"} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 170, cellStyle: {textAlign: "left"} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 170, cellStyle: {textAlign: "left"} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 200, cellStyle: {textAlign: "left"} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"} },
    { headerName: "ERP Stock Qty", headerClass: "hd-ta-right", field: "stockQtyErp", width: 150, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
    { headerName: "WMS Stock Qty", headerClass: "hd-ta-right", field: "stockQtyWms", width: 150, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
    { headerName: "Diff Qty", headerClass: "hd-ta-right", field: "", width: 150, summable: true,
      valueGetter: (p: any) => {
        return p.data.stockQtyErp - p.data.stockQtyWms;
      },
      cellStyle: (p: any) => {
        return { textAlign: "right", ...(p.data.matchYn != "Y" && { color: "red" }) };
      }
    },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    }
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/stock/inventory_compare.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/stock/inventory_compare", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/stock/inventory_compare", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/stock/inventory_compare", {});
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/stock/inventory_compare.download?mode=search", searchParams, {
        "fileName": "ERP재고비교_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>ERP 재고 비교</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div>
            <p>기준일자</p>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD"
                  value={searchParams.stockDate ? dayjs(searchParams.stockDate, "YYYYMMDD") : null}
                  onChange={(e) => {doSearch(e, {stockDate: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                />
            </LocalizationProvider>
          </div>
          <div>
            <p>창고</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.wh}
                onChange={(e) => {doSearch(e, {wh: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.wh as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>P/N</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.pnLike}
              onChange={(e) => {updateSearchParams({ pnLike: e.target.value })}}
              onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
              placeholder="P/N"
            />
          </div>
          <div>
            <p>TR No</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.trNoLike}
              onChange={(e) => {updateSearchParams({ trNoLike: e.target.value })}}
              onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
              placeholder="TR No"
            />
          </div>
          <div>
            <p>S/N</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.snLike}
              onChange={(e) => {updateSearchParams({ snLike: e.target.value })}}
              onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
              placeholder="S/N"
            />
          </div>
          <div>
            <p>재고일치</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.matchYn}
                onChange={(e) => {doSearch(e, {matchYn: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.matchYn as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 재고실사 목록 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              onStateUpdated={gridCallbacks.onStateUpdated}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {/*// 재고실사 목록 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(InventoryCompare);
